/**
 * citationMarkerWriter — wrap inline ``[N]`` / ``[Smith2024]`` citation
 * markers in a freshly inserted section with Word inline content controls
 * tagged ``asthra-cite:<sectionNumber>:<bracketBody>``. The content
 * controls are how we make the markers clickable: ``ContentControl.onEntered``
 * fires when the writer's cursor lands inside one, and the click handler
 * walks back to the previous marker or section heading to assemble the
 * "highlighted" context for the citation lookup.
 *
 * # CONTRACT
 *  - Idempotent: re-running on a section whose markers are already wrapped
 *    is a no-op. ``insertSectionContent`` overwrites the section's content
 *    before delegating here, so the only "already wrapped" case in practice
 *    is the section's content controls that survived a partial sync — we
 *    detect them by tag prefix and skip those token positions.
 *  - Skips registered flag markers ([MISSING INFO: …], [REVIEWER: …],
 *    [NOT FOUND - …], [FILL: …], …) — those are NOT citations and must
 *    not become clickable. Uses ``matchFlagMarker`` from draftSanitizer.
 *  - Skips ``[1]:``-style markdown reference links at line start (mirror
 *    of the renumber pass).
 *  - Skips bracket tokens that don't match a citation shape (numeric list,
 *    numeric range, or author-year tag) — same shape catalogue the
 *    renumber pass treats as a citation.
 *
 * # WHY a post-write pass rather than per-segment wrapping
 *  Wrapping inside ``renderSegment`` would require threading
 *  ``sectionNumber`` through the renderer (which is shared by both
 *  variants) and would have to handle the Office.js content-control-
 *  insert-while-still-inserting-text gotchas. A post-write pass anchored
 *  to the section's ``ASTHRA_SEC_*`` bookmark is simpler and keeps the
 *  shared renderer variant-agnostic.
 */

import {
  CITATION_BRACKET_PATTERN,
  classifyCitationBracket,
  matchFlagMarker,
} from "./draftSanitizer";
import { reportWordFailure } from "../../services/wordOpsLogger";

/** Tag prefix every citation content control carries. The full tag is
 *  ``asthra-cite:<sectionNumber>:<bracketBody>`` — e.g. ``asthra-cite:9.2:1``
 *  or ``asthra-cite:3.2.S:Smith2024``. The prefix is exported so the events
 *  service can enumerate "all citation controls" with a single string check. */
export const CITATION_CC_TAG_PREFIX = "asthra-cite:";

/** Build a content-control tag for a citation marker. Round-trippable via
 *  ``parseCitationTag`` so the events handler knows which section the click
 *  belongs to. The section number can contain dots (``9.2``, ``3.2.S.1``);
 *  the bracket body is the canonical key from ``classifyCitationBracket``
 *  (``"1"``, ``"1,2"``, ``"1-3"``, ``"Smith2024"``). Section number and key
 *  are joined by ``:``; their internal characters never contain ``:``. */
export function citationTag(sectionNumber: string, key: string): string {
  return `${CITATION_CC_TAG_PREFIX}${sectionNumber}:${key}`;
}

/** Parse a citation content-control tag back into ``{sectionNumber, key}``.
 *  Returns null for any tag that doesn't carry our prefix or is malformed.
 *  The first ``:`` after the prefix splits section from key. */
export function parseCitationTag(
  tag: string,
): { sectionNumber: string; key: string } | null {
  if (!tag || !tag.startsWith(CITATION_CC_TAG_PREFIX)) return null;
  const rest = tag.slice(CITATION_CC_TAG_PREFIX.length);
  const splitAt = rest.indexOf(":");
  if (splitAt <= 0 || splitAt === rest.length - 1) return null;
  return { sectionNumber: rest.slice(0, splitAt), key: rest.slice(splitAt + 1) };
}

/** Office.js host availability probe — keeps callers from importing
 *  ``isOfficeAvailable`` from wordWriter.ts (which would pull the whole
 *  module into variant-c). */
function isOfficeReady(): boolean {
  return (
    typeof Word !== "undefined" &&
    typeof Word.run === "function" &&
    typeof Office !== "undefined" &&
    Office.context !== undefined
  );
}

/** Compute the section's bookmark name. Mirrors ``sectionBookmarkName`` in
 *  wordWriter.ts but inlined here so this module doesn't pull a non-trivial
 *  transitive dependency from the shared writer for one string concat.
 *  Keep in sync with ``ASTHRA_SEC_PREFIX``. */
function bookmarkNameFor(sectionNumber: string): string {
  return `ASTHRA_SEC_${sectionNumber.replace(/-/g, "__").replace(/\./g, "_")}`;
}

/** A single ``[N]`` (or ``[Smith2024]``) match the wrapper should turn into
 *  a content control. Built from a paragraph's text via regex. */
interface CitationMatch {
  /** The literal bracket text (e.g. ``"[3]"``, ``"[1, 5]"``) to search for
   *  in Word. Used both for ``Range.search`` and for our internal
   *  occurrence counter when the same literal appears multiple times in
   *  one paragraph. */
  literal: string;
  /** Canonical key used in the content-control tag — ``"1"``, ``"1,2"``,
   *  ``"1-3"``, ``"Smith2024"`` — produced by ``classifyCitationBracket``. */
  key: string;
}

interface ParagraphHeadingSnapshot {
  text: string;
  style?: string;
  styleBuiltIn?: string;
}

/** Pure helper: pull the citation tokens out of a paragraph's text in the
 *  order they appear. Exported for unit-style testability in node — the
 *  Office.js code path consumes the same result. */
export function extractCitationMatches(text: string): CitationMatch[] {
  if (!text || text.indexOf("[") === -1) return [];
  // CITATION_BRACKET_PATTERN is shared with the renumberer (/g flag). Reset
  // its lastIndex before iterating so successive calls don't skip leading
  // matches. The match's group 1 is the bracket body; match[0] is the full
  // literal ``"[…]"``.
  CITATION_BRACKET_PATTERN.lastIndex = 0;
  const matches: CitationMatch[] = [];
  let m: RegExpExecArray | null;
  while ((m = CITATION_BRACKET_PATTERN.exec(text)) !== null) {
    const literal = m[0];
    const body = m[1];
    // Markdown reference-link at line start (``[1]: url``) — never wrap.
    const atLineStart = m.index === 0 || text.charAt(m.index - 1) === "\n";
    if (atLineStart && text.charAt(m.index + literal.length) === ":") continue;
    // Registered flag marker — never wrap.
    if (matchFlagMarker(literal)) continue;
    // Citation shape? If not, skip (conservative — matches the renumber pass).
    const key = classifyCitationBracket(body);
    if (key === null) continue;
    matches.push({ literal, key });
    // Guard against a pathological zero-width match stalling the loop.
    if (m.index === CITATION_BRACKET_PATTERN.lastIndex) {
      CITATION_BRACKET_PATTERN.lastIndex += 1;
    }
  }
  return matches;
}

export function findHeadingIndexByText(
  paragraphs: ParagraphHeadingSnapshot[],
  sectionNumber: string,
): number {
  const headingPrefix = `${sectionNumber} `;
  const headingPrefixDouble = `${sectionNumber}  `;
  for (let i = 0; i < paragraphs.length; i++) {
    const para = paragraphs[i];
    const builtIn = (para.styleBuiltIn || "").toLowerCase();
    const display = (para.style || "").toLowerCase();
    const isHeading = builtIn.includes("heading") || display.includes("heading");
    const text = para.text.trim();
    if (isHeading && (text.startsWith(headingPrefix) || text.startsWith(headingPrefixDouble))) {
      return i;
    }
  }
  return -1;
}

/** Wrap every citation marker in the section's content with an inline
 *  content control tagged ``asthra-cite:<section>:<key>``. Idempotent —
 *  paragraphs whose markers are already wrapped are detected via the
 *  ``CITATION_CC_TAG_PREFIX`` check and skipped.
 *
 *  Resolves to the number of new content controls created (0 when the
 *  section has no citations or all are already wrapped). Errors are
 *  swallowed and logged — citation marking is best-effort and must
 *  never block ``insertSectionContent`` from advancing the lifecycle. */
export async function wrapCitationMarkers(sectionNumber: string): Promise<number> {
  if (!isOfficeReady()) return 0;
  if (!sectionNumber) return 0;
  console.log(`[citationMarkerWriter] wrap start §${sectionNumber}`);
  try {
    return await Word.run(async (context) => {
      const bookmarkName = bookmarkNameFor(sectionNumber);
      const bookmarkRange = context.document.getBookmarkRangeOrNullObject(bookmarkName);
      bookmarkRange.load("isNullObject");
      await context.sync();
      if (bookmarkRange.isNullObject) {
        console.warn(
          `[citationMarkerWriter] no bookmark ${bookmarkName} for section §${sectionNumber}`,
        );
        return 0;
      }

      // Section's content lives between the heading paragraph (the one the
      // bookmark sits on) and the next heading-styled paragraph — the
      // boundary ``insertSectionContent`` uses when it deletes-then-inserts.
      const spanParas =
        (await sectionSpanFromBookmark(context, bookmarkRange, sectionNumber)) ??
        (await sectionSpanByFullScan(context, bookmarkRange, sectionNumber));
      if (!spanParas) {
        console.warn(
          `[citationMarkerWriter] bookmark ${bookmarkName} did not resolve to a paragraph`,
        );
        return 0;
      }

      return await wrapCitationsInParagraphs(context, sectionNumber, spanParas);
    });
  } catch (err) {
    reportWordFailure("citationMarkerWriter.wrapCitationMarkers", err);
    console.warn(
      `[citationMarkerWriter] wrap failed for §${sectionNumber} (non-fatal):`,
      err,
    );
    return 0;
  }
}

/**
 * Wrap the citation markers in ``spanParas`` (one section's body) inside an
 * existing request context — 2–3 syncs, no section lookup. ``insertSectionContent``
 * calls this on the paragraphs it just inserted, in the same ``Word.run``, so a
 * fresh section skips the bookmark lookup and tail load of
 * {@link wrapCitationMarkers}. ``opts.textLoaded`` skips the text load when the
 * caller already loaded it; ``opts.fresh`` skips the already-wrapped check
 * (freshly inserted content carries no citation controls). Throws on Office
 * errors — callers decide whether that is fatal.
 */
export async function wrapCitationsInParagraphs(
  context: Word.RequestContext,
  sectionNumber: string,
  spanParas: Word.Paragraph[],
  opts: { textLoaded?: boolean; fresh?: boolean } = {},
): Promise<number> {
  // For each prose paragraph in the section, find citation tokens in
  // the paragraph text (JS-side regex), then locate each in Word via
  // ``Range.search`` and wrap with a content control. Existing content
  // controls inside the paragraph are loaded so we can skip already-
  // wrapped tokens (idempotent re-runs).
  let created = 0;
  // Diagnostic counters: distinguish "no citations to wrap" (legitimate
  // silence) from "citations were in the prose but none got wrapped"
  // (the silent-skip case a missing/misaligned bookmark produces).
  let totalRegexMatches = 0;
  let anyAlreadyWrapped = false;
  // Text + existing inline content controls for every span paragraph in
  // ONE sync, so the wrap pass doesn't re-sync per paragraph.
  const existingCCs: Array<Word.ContentControlCollection | null> = [];
  for (const p of spanParas) {
    if (!opts.textLoaded) p.load("text");
    if (opts.fresh) {
      existingCCs.push(null);
      continue;
    }
    const ccs = p.getRange("Whole").contentControls;
    ccs.load("items/tag");
    existingCCs.push(ccs);
  }
  if (!opts.textLoaded || !opts.fresh) await context.sync();

  // Queue every paragraph's searches, ONE sync, then wrap. ``Range.search``
  // returns matches in document order; when the same literal (e.g.
  // ``[1]``) appears N times in one paragraph, the i-th search result
  // corresponds to the i-th regex match for that literal. PERF: this used
  // to sync once per citation-bearing paragraph — on Word desktop every
  // sync is a round trip to Word's UI thread.
  const searches: Array<{
    literal: string;
    results: Word.RangeCollection;
    group: CitationMatch[];
    alreadyWrappedTags: Set<string>;
  }> = [];
  for (let i = 0; i < spanParas.length; i++) {
    const para = spanParas[i];
    const matches = extractCitationMatches(para.text);
    totalRegexMatches += matches.length;
    if (matches.length === 0) continue;

    // Already-wrapped guard: if the paragraph already carries content
    // controls with our prefix, skip those tags so a re-run doesn't
    // double-wrap. (Most of the time this collection is empty.)
    const wrapped = existingCCs[i];
    const alreadyWrappedTags = new Set<string>();
    if (wrapped?.items) {
      for (const cc of wrapped.items) {
        if (cc.tag?.startsWith(CITATION_CC_TAG_PREFIX)) {
          alreadyWrappedTags.add(cc.tag);
          anyAlreadyWrapped = true;
        }
      }
    }

    // Group matches by literal so identical brackets get located via a
    // single ``Range.search`` call and dispatched to results in order.
    const groups = new Map<string, CitationMatch[]>();
    for (const cm of matches) {
      const list = groups.get(cm.literal) ?? [];
      list.push(cm);
      groups.set(cm.literal, list);
    }

    // The Office.js Web SDK rejects ``matchWildcards: true`` for
    // ``[``-bearing queries on some versions; use literal matching
    // (matchCase, matchWholeWord false) which is what we want anyway.
    for (const [literal, group] of groups) {
      const results = para.search(literal, {
        matchCase: true,
        matchWildcards: false,
      });
      results.load("items");
      searches.push({ literal, results, group, alreadyWrappedTags });
    }
  }
  if (searches.length > 0) await context.sync();

  for (const { literal, results, group, alreadyWrappedTags } of searches) {
    // Office.js may return fewer search results than regex matches if
    // the paragraph text contains Word-internal characters that don't
    // round-trip; fall back to whichever count is smaller so we never
    // index past the end. The mismatch is rare; we log when it bites.
    const count = Math.min(results.items.length, group.length);
    if (count < group.length) {
      console.warn(
        `[citationMarkerWriter] §${sectionNumber}: search returned ${results.items.length} hits for ${literal} but regex saw ${group.length}; wrapping ${count}`,
      );
    }
    for (let k = 0; k < count; k++) {
      const cm = group[k];
      const targetTag = citationTag(sectionNumber, cm.key);
      // Skip if a control with this exact tag already exists somewhere
      // in the paragraph (idempotent re-runs). Per-literal tags carry
      // the key, so duplicate keys (writer-typed bursts of ``[1]``)
      // still wrap correctly across distinct paragraphs.
      if (alreadyWrappedTags.has(targetTag)) continue;
      const range = results.items[k];
      const cc = range.insertContentControl();
      cc.tag = targetTag;
      cc.title = "Citation";
      // Hidden appearance — keep the plain ``[N]`` text in the doc
      // with NO bounding-box brackets and NO tag chrome. The earlier
      // concern (``Hidden`` + ``cc.onEntered`` = no event) is no
      // longer relevant: ``citationMarkerEvents`` polls the
      // selection-change event and reads
      // ``selection.parentContentControlOrNullObject`` to detect
      // cursor-into-marker. That works regardless of appearance —
      // the cursor still lands "inside" a hidden CC. See that
      // module for details.
      cc.appearance = Word.ContentControlAppearance.hidden;
      cc.cannotEdit = false;
      cc.cannotDelete = false;
      created += 1;
    }
  }

  if (created > 0) await context.sync();
  // Silent-skip diagnostic: prose carried citation tokens but none were
  // wrapped AND no prior controls explain the zero. Most often: the
  // section's ASTHRA_SEC_* bookmark didn't resolve to a real paragraph,
  // or the heading-boundary walk landed in the wrong span — both render
  // as the [1] vs ⟦1⟧ inconsistency users see.
  if (totalRegexMatches > 0 && created === 0 && !anyAlreadyWrapped) {
    console.warn(
      `[citationMarkerWriter] §${sectionNumber}: ${totalRegexMatches} citation tokens found in prose but 0 wrapped — check bookmark/section boundary`,
    );
  }
  console.log(
    `[citationMarkerWriter] wrap done §${sectionNumber}: created=${created} regexMatches=${totalRegexMatches} alreadyWrapped=${anyAlreadyWrapped}`,
  );
  return created;
}

/** Most paragraphs read after a section heading when looking for the next
 *  heading (table-cell paragraphs count). Mirrors wordWriter's cap. */
const SPAN_TAIL_CAP = 2000;

/** Heading test shared by both span finders (built-in or custom style name). */
function isHeadingParagraph(p: Word.Paragraph): boolean {
  const builtIn = (p.styleBuiltIn || "").toLowerCase();
  const display = (p.style || "").toLowerCase();
  return builtIn.includes("heading") || display.includes("heading");
}

/**
 * The section body's paragraphs (heading excluded, next heading excluded),
 * located from the bookmark itself: the heading paragraph is the bookmark's
 * first paragraph, and only the stretch from it to the end of the document is
 * read — style names only, no per-paragraph proxies. Null when the host
 * rejects the range ops; the caller then falls back to the full scan.
 *
 * PERF: replaces a whole-document text load plus a ``compareLocationWith`` for
 * EVERY paragraph (headings are title-only under native numbering, so the text
 * match always missed) — O(document) proxies per section write, which froze
 * Word desktop for seconds on each section of a long draft.
 */
async function sectionSpanFromBookmark(
  context: Word.RequestContext,
  bookmarkRange: Word.Range,
  sectionNumber: string,
): Promise<Word.Paragraph[] | null> {
  try {
    const tail = bookmarkRange.paragraphs
      .getFirst()
      .getRange("Whole")
      .expandTo(context.document.body.getRange("End")).paragraphs;
    // Capped: without ``top`` this marshals every paragraph to the end of the
    // document (the whole body for an early section).
    tail.load({ select: "style,styleBuiltIn", top: SPAN_TAIL_CAP });
    await context.sync();
    if (tail.items.length === 0) return null;
    let end = -1;
    for (let i = 1; i < tail.items.length; i++) {
      if (isHeadingParagraph(tail.items[i])) {
        end = i;
        break;
      }
    }
    if (end === -1) {
      // No next heading within the cap: either the last section (the span runs
      // to the end of the document) or one longer than the cap — let the full
      // scan decide rather than wrap a truncated span.
      if (tail.items.length >= SPAN_TAIL_CAP) return null;
      end = tail.items.length;
    }
    return tail.items.slice(1, end);
  } catch (err) {
    reportWordFailure("citationMarkerWriter.bookmarkSpan", err);
    console.warn(
      `[citationMarkerWriter] §${sectionNumber}: bookmark-scoped span failed — falling back to full scan:`,
      err,
    );
    return null;
  }
}

/** Legacy span finder: load every body paragraph, find the heading by its
 *  number prefix or, failing that, by comparing each paragraph to the
 *  bookmark. Kept only as the fallback for {@link sectionSpanFromBookmark}. */
async function sectionSpanByFullScan(
  context: Word.RequestContext,
  bookmarkRange: Word.Range,
  sectionNumber: string,
): Promise<Word.Paragraph[] | null> {
  const paragraphs = context.document.body.paragraphs;
  paragraphs.load("text,style,styleBuiltIn");
  await context.sync();

  let headingIdx = findHeadingIndexByText(paragraphs.items, sectionNumber);
  if (headingIdx === -1) {
    const comparisons: OfficeExtension.ClientResult<Word.LocationRelation>[] = [];
    for (const p of paragraphs.items) {
      comparisons.push(bookmarkRange.compareLocationWith(p.getRange("Whole")));
    }
    await context.sync();
    for (let i = 0; i < comparisons.length; i++) {
      const v = comparisons[i].value;
      if (v === "InsideStart" || v === "Equal" || v === "Inside") {
        headingIdx = i;
        break;
      }
    }
  }
  if (headingIdx === -1) return null;

  let nextHeadingIdx = paragraphs.items.length;
  for (let i = headingIdx + 1; i < paragraphs.items.length; i++) {
    if (isHeadingParagraph(paragraphs.items[i])) {
      nextHeadingIdx = i;
      break;
    }
  }
  return paragraphs.items.slice(headingIdx + 1, nextHeadingIdx);
}
